import {INLINE_ERROR_SELECTOR} from '../InlineError';

const FOCUSABLE = [
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

// A group (a `<Label role="radiogroup">`, or an `OptionButtonGroup`) is marked
// invalid as a whole, so focus goes to the option that is checked, or else to
// the first one.
const focusTargetOf = (invalid: HTMLElement): HTMLElement | null =>
  invalid.matches(FOCUSABLE)
    ? invalid
    : (invalid.querySelector<HTMLElement>(
        'input:checked, [aria-checked="true"]',
      ) ?? invalid.querySelector<HTMLElement>(FOCUSABLE));

const FIELD_SELECTOR = '[aria-invalid="true"]';

export type FocusedField = {
  /**
   * The field that has focus now: the first one with an error that could take
   * it. A group is the element that is marked, not the option that has focus.
   */
  field: HTMLElement;
  /**
   * Whether focus was already in `field`, so that it did not move into it.
   * A screen reader reads a field and its error when focus moves into it, and
   * says nothing when focus was there already.
   */
  wasFocused: boolean;
};

/**
 * Moves focus to the first field in `container` that is marked
 * `aria-invalid="true"`, so that after a failed submit a screen reader lands on
 * the field and reads its error. Every field under a `<Label>` with an error is
 * marked that way. A field that can't take focus (one in a collapsed section,
 * say) is skipped for the next.
 *
 * Returns the field that has focus, and whether focus was in it before, or
 * `null` when no field could take focus.
 */
export const focusFirstInvalidField = (
  container: HTMLElement | null,
): FocusedField | null => {
  if (!container) {
    return null;
  }
  const before = document.activeElement;
  for (const field of container.querySelectorAll<HTMLElement>(FIELD_SELECTOR)) {
    const target = focusTargetOf(field);
    target?.focus();
    if (target && document.activeElement === target) {
      return {field, wasFocused: field.contains(before)};
    }
  }
  return null;
};

/**
 * Like `focusFirstInvalidField`, for a caller that only needs to know whether
 * a field has focus now. That is also so when focus was there already.
 */
export const focusFirstInvalid = (container: HTMLElement | null): boolean =>
  focusFirstInvalidField(container) !== null;

/** The first field in `container` that is marked as not valid, or `null`. */
export const firstInvalidField = (
  container: HTMLElement | null,
): HTMLElement | null =>
  container?.querySelector<HTMLElement>(FIELD_SELECTOR) ?? null;

/**
 * The text of the error that `field` shows: what its `aria-describedby` points
 * to that is an `InlineError`, so that its help text is left out. Empty when it
 * has none.
 */
export const errorTextOf = (field: HTMLElement): string => {
  const texts: Array<string> = [];
  for (const id of (field.getAttribute('aria-describedby') ?? '').split(
    /\s+/,
  )) {
    const element = id ? field.ownerDocument.getElementById(id) : null;
    if (element?.matches(INLINE_ERROR_SELECTOR)) {
      texts.push((element.textContent ?? '').trim());
    }
  }
  return texts.filter(Boolean).join(' ');
};
